/* ═══════════════════════════════════════════════════════════════════════════
   THE INDUSTRY REGISTRY

   One record per industry. This is the file that makes fourteen industries a
   data-entry job instead of fourteen builds, and it is the JavaScript twin of
   the `avtale_industries` table in NETTSIDE-SPEC.md — same columns, same
   meanings. When the backend serves that table, `fetchIndustries()` returns
   these same rows over HTTP and this file becomes the fixture.

   THE RULE THAT KEEPS THIS FROM ROTTING

     No component, layout or section may name an industry. Nothing anywhere
     above this file may contain `industry === 'hair'`. Everything that varies
     comes from `words`, `sections`, `layout` and `theme`.

   There is a test that greps for exactly that (`industries.test.js`), because
   the rule is only worth as much as its enforcement.

   ── The columns ──────────────────────────────────────────────────────────

   code          hair, legal, home … — the tenant's `avtale_businesses.industry`
   name          what a human calls it, in English, for menus and headings
   layout        booking | engagement | site_visit | menu — the page recipe
   parent        the trade this one sits under, where it is a speciality of a
                 broader family rather than a family of its own. A heat pump
                 firm IS a trades firm — same site visit, same written quote,
                 same law — and a customer who searches for a tradesman should
                 find one. So the child inherits the parent's search words and
                 says on its own page which family it belongs to, while
                 keeping its own tile, hero and photographs. Absent on a trade
                 that stands alone, which is most of them
   theme         which token set the website starts from
   price_visible does the public page show prices at all
   picks_person  may the CUSTOMER choose who does the work. True where the
                 person is the thing being chosen — a hairdresser, a
                 physiotherapist, a named lawyer. False where the customer
                 engages the FIRM and the firm allocates: a builder sends
                 whichever joiner is free that week, and a restaurant gives
                 you a table rather than a waiter. Where it is false the
                 public page lists no individuals, the booking names none,
                 and nothing offers to book one — see domain/roster.js
   standard      which contract standard the engagement falls under; see
                 domain/document.js. null means the generic template
   sections      the blocks of the public page, in order
   tile          the glyph and the one-line description on the front page's
                 category tiles. The prototype drew nine by hand; here every
                 trade carries its own, so the section is whatever the registry
                 holds and a fifteenth trade appears without anyone editing a
                 component
   hero          the front page when this trade is chosen: strip, kicker, headline
                 (in two parts, the second one in the accent colour) and the
                 lead paragraph. This is what makes fourteen industries
                 legible — the page says which world you are in
   search        what people actually call this trade when they search for it.
                 Norwegian on purpose, and the one place in the app where that
                 is right: it is not text anybody reads, it is the words a
                 Norwegian customer types. See domain/search.js
   words         the vocabulary. Every noun the surfaces use comes from here
   disclosure    what this industry is allowed to be told about a customer.
                 `address` only where the work happens in the customer's home,
                 `over18` where an age limit is a legal condition, `age` where
                 the number is professionally relevant. Nothing else, ever.
   ═══════════════════════════════════════════════════════════════════════ */

/** Default vocabulary. An industry overrides only the words it disagrees
    with, so a new record is five lines and not fifty. */
const BASE_WORDS = {
  service: 'service',
  services: 'Services',
  staff_member: 'member of staff',
  staff: 'The team',
  book: 'BOOK APPOINTMENT',
  appointment: 'appointment',
  appointments: 'Appointments',
  appointment_doc: 'Confirmed appointment',
  customer: 'customer',
  customers: 'Customers',
  customer_number: 'Customer number',
  calendar: 'Appointment book',
  visit: 'completed appointment',
  visits: 'Completed appointments',
  visit_unit: 'appointment',
  note: 'note',
  history: 'History',
  correspondence: 'Messages',
  file: 'customer card',
  files: 'Customer cards',
  line: 'Service',
  new: 'New appointment',
  enquiry: 'ENQUIRE',
};

/** What every customer may do, whatever the industry. These come from
    personopplysningsloven § 15 and GDPR art. 15, so they are not an industry's
    to opt out of — an industry may only add to them. */
const BASE_RIGHTS = [
  'You can ask to see whatever has been noted about you.',
  'You can ask for it to be corrected, or deleted.',
  'You can see exactly which of your details this business was given, and when.',
];

/** What the business had to put on the table before it could trade. */
const BASE_PAPERS = [
  ['Organisation number', 'Checked against Brønnøysundregistrene'],
];

const NO_ADDRESS = { address: false, over18: false, age: false };
const AT_HOME_D = { address: true, over18: false, age: false };

/* ── The fourteen ────────────────────────────────────────────────────────
   Sorted the way a customer would look for them, not alphabetically. */
const RECORDS = [
  {
    code: 'hair', name: 'Hair & barber', layout: 'booking', theme: 'studio',
    price_visible: true, picks_person: true, standard: null,
    bht: 'listed',
    sections: ['hero', 'services', 'staff', 'booking', 'reviews', 'products', 'posts', 'contact'],
    search: ['frisør', 'barber', 'klipp', 'farge', 'hår', 'salong', 'styling'],
    tile: { icon: '✂', sub: 'Cut · colour · styling' },
    hero: {
      strip: 'Free times today — book in under a minute',
      kicker: 'HAIR & BARBER',
      title: 'Find your hairdresser.', em: 'Book today.',
      lead: 'See free times, the price and how long it takes before you book. The appointment is confirmed with your mobile number.',
    },
    words: {
      service: 'treatment', services: 'Treatments',
      staff_member: 'hairdresser', staff: 'The hairdressers',
      visit: 'completed treatment', visits: 'Completed appointments',
      visit_unit: 'treatment', note: 'treatment note', history: 'Treatment history',
      line: 'Treatment and products',
    },
    rights: [
      'Products you buy are covered by forbrukerkjøpsloven.',
      'Cancellation terms must be stated before you book.',
    ],
    papers: [['Product information', 'Ingredient list on the products you buy']],
    disclosure: NO_ADDRESS,
  },
  {
    code: 'beauty', name: 'Skin, nails & beauty', layout: 'booking', theme: 'studio',
    price_visible: true, picks_person: true, standard: null,
    bht: 'listed',
    sections: ['hero', 'services', 'staff', 'booking', 'reviews', 'products', 'posts', 'contact'],
    search: ['hudpleie', 'negler', 'bryn', 'vipper', 'skjønnhet', 'voksing', 'makeup'],
    tile: { icon: '◇', sub: 'Nails · brows · skin' },
    hero: {
      strip: 'Free skin analysis before every treatment',
      kicker: 'SKIN, NAILS & BEAUTY',
      title: 'Beauty on your', em: 'own terms.',
      lead: 'Skin, nails and brows with the price and the duration stated. Pick a time and confirm it in under a minute.',
    },
    words: {
      service: 'treatment', services: 'Treatments',
      staff_member: 'therapist', staff: 'The therapists',
      visit: 'completed treatment', visits: 'Completed appointments',
      visit_unit: 'treatment', note: 'treatment note', history: 'Treatment history',
      line: 'Treatment and products',
    },
    disclosure: NO_ADDRESS,
  },
  {
    code: 'massage', name: 'Massage & wellbeing', layout: 'booking', theme: 'studio',
    price_visible: true, picks_person: true, standard: null,
    bht: 'risk',
    sections: ['hero', 'services', 'staff', 'booking', 'reviews', 'products', 'posts', 'contact'],
    search: ['massasje', 'terapeut', 'velvære', 'muskler', 'spa'],
    tile: { icon: '≈', sub: 'Wellbeing · treatment' },
    hero: {
      strip: 'Evening times free — book directly',
      kicker: 'MASSAGE & WELLBEING',
      title: 'Time to', em: 'breathe out.',
      lead: 'Massage, muscle treatment and wellbeing. See what is free today and book without ringing anybody.',
    },
    words: {
      service: 'treatment', services: 'Treatments',
      staff_member: 'therapist', staff: 'The therapists',
      visit: 'completed treatment', visits: 'Completed appointments',
      visit_unit: 'treatment', note: 'treatment note', history: 'Treatment history',
    },
    /* The therapist comes to the house for home visits, so the address is
       part of the job. */
    disclosure: AT_HOME_D,
  },
  {
    code: 'health', name: 'Health & medical', layout: 'booking', theme: 'clinical',
    price_visible: true, picks_person: true, standard: null,
    bht: 'listed',
    sections: ['hero', 'services', 'staff', 'booking', 'reviews', 'posts', 'contact'],
    search: ['helse', 'lege', 'fysioterapi', 'kiropraktor', 'klinikk', 'behandling'],
    tile: { icon: '＋', sub: 'Clinics · therapists' },
    hero: {
      strip: 'Short waiting times — the price is registered automatically',
      kicker: 'HEALTH & MEDICAL',
      title: 'Your health.', em: 'Our priority.',
      lead: 'Physiotherapy, chiropractic and general practice. Book a consultation and see the price before you arrive.',
    },
    words: {
      service: 'consultation', services: 'Consultations',
      staff_member: 'clinician', staff: 'The clinicians',
      visit: 'completed consultation', visits: 'Completed consultations',
      visit_unit: 'consultation', note: 'note', history: 'Consultation history',
      book: 'BOOK CONSULTATION', new: 'New consultation',
    },
    rights: [
      'Pasient- og brukerrettighetsloven gives you access to your own record.',
      'You can ask which treatment alternatives were considered, and why.',
    ],
    papers: [['Authorisation', 'Checked against Helsepersonellregisteret']],
    /* Age is professionally relevant here — the number, never the date. */
    disclosure: { address: false, over18: false, age: true },
  },
  {
    code: 'training', name: 'Training & fitness', layout: 'booking', theme: 'studio',
    price_visible: true, picks_person: true, standard: null,
    bht: 'risk',
    sections: ['hero', 'services', 'staff', 'booking', 'reviews', 'products', 'posts', 'contact'],
    search: ['trening', 'gym', 'yoga', 'pilates', 'personlig trener', 'studio'],
    tile: { icon: '◎', sub: 'PT · classes · courses' },
    hero: {
      strip: 'Free trial session — no tie-in',
      kicker: 'TRAINING & FITNESS',
      title: 'Get started.', em: 'And keep it going.',
      lead: 'Personal training, classes and programmes. Book a single session — see the price before you commit to anything.',
    },
    words: {
      service: 'class', services: 'Classes',
      staff_member: 'instructor', staff: 'The instructors',
      customer: 'member', customers: 'Members', customer_number: 'Member number',
      calendar: 'Timetable', appointment: 'class', appointments: 'Classes',
      visit: 'attended class', visits: 'Attended classes', visit_unit: 'class',
      book: 'BOOK A CLASS', new: 'New class',
    },
    disclosure: { address: false, over18: false, age: true },
  },
  {
    code: 'restaurant', name: 'Restaurant & café', layout: 'menu', theme: 'restaurant',
    price_visible: true, picks_person: false, standard: null,
    bht: 'listed',
    /* No `staff` band. A guest is given a TABLE, not a waiter — `picks_person`
       already says so — and a public page listing six waiters with ratings
       and response times under a heading that promises tables was telling
       the guest something they cannot act on and the restaurant something it
       never agreed to publish. Who is on the floor on Friday is the
       restaurant's own information. See `sectionsFor`, which now enforces
       this rather than leaving it to whoever edits this row. */
    sections: ['hero', 'menu', 'booking', 'reviews', 'products', 'posts', 'contact'],
    search: ['restaurant', 'kafé', 'spise', 'bord', 'meny', 'bistro', 'pizza', 'middag'],
    tile: { icon: '⌂', sub: 'Tables · experiences' },
    hero: {
      strip: 'Reserve a table in seconds — confirmation by SMS',
      kicker: 'RESTAURANT & CAFÉ',
      title: 'Reserve a table.', em: 'Without the phone queue.',
      lead: 'See what is free, the menu and the allergens. Reserve in seconds and get it in writing.',
    },
    words: {
      service: 'dish', services: 'Menu',
      staff_member: 'table', staff: 'The tables',
      customer: 'guest', customers: 'Guests',
      calendar: 'Table book', appointment: 'table', appointments: 'Reservations',
      appointment_doc: 'Confirmed reservation',
      visit: 'completed visit', visits: 'Previous visits', visit_unit: 'visit',
      book: 'RESERVE A TABLE', new: 'New reservation',
      file: 'guest card', files: 'Guest cards',
    },
    rights: [
      'Allergen information must be given on request, for every dish.',
      'A reservation can be cancelled on the terms stated when you booked.',
    ],
    papers: [['Serving licence', 'Granted by the municipality under alkoholloven']],
    /* Serving alcohol is conditional on age, so the answer is disclosed and
       the date of birth never is — alkoholloven asks whether, not when. */
    disclosure: { address: false, over18: true, age: false },
  },
  {
    code: 'home', name: 'Trades & construction', layout: 'site_visit', theme: 'service',
    price_visible: false, picks_person: false, standard: 'trade',
    bht: 'listed',
    sections: ['hero', 'services', 'enquiry', 'reviews', 'products', 'posts', 'contact'],
    search: ['håndverker', 'bygg', 'snekker', 'rørlegger', 'elektriker', 'oppussing', 'maler'],
    tile: { icon: '⌁', sub: 'Service · maintenance' },
    hero: {
      strip: 'Free site visit and a written quote before work starts',
      kicker: 'TRADES & CONSTRUCTION',
      title: 'Trades who come', em: 'to you.',
      lead: 'Building, plumbing, electrical and the small jobs. Ask for a site visit and a written price before anything starts.',
    },
    words: {
      service: 'job', services: 'What we do',
      staff_member: 'tradesman', staff: 'The team',
      calendar: 'Jobs', appointment: 'job', appointments: 'Jobs',
      appointment_doc: 'Written agreement',
      visit: 'completed job', visits: 'Completed jobs', visit_unit: 'job',
      book: 'REQUEST A SITE VISIT', new: 'New job',
      history: 'Job history', line: 'Work and materials',
    },
    rights: [
      'Håndverkertjenesteloven gives you a written agreement and a price estimate before work starts.',
      'An estimate may not be exceeded by more than 15 per cent.',
      'Five years of complaint rights on work on a permanent dwelling.',
    ],
    papers: [['Central approval', 'Ansvarsrett, checked against Direktoratet for byggkvalitet']],
    /* He is coming to the house. Without the address there is no job. */
    disclosure: AT_HOME_D,
  },
  {
    /* A speciality of `home` rather than a trade of its own: the same site
       visit, the same written quote before work starts, the same
       håndverkertjenesteloven, and the firm allocates the fitter. What it
       does not share is how a customer looks for it — nobody searches for a
       tradesman when their house is cold, they search for a heat pump — and
       that is the whole reason it has a tile. */
    code: 'heating', name: 'Ventilation & heating', layout: 'site_visit', theme: 'service',
    parent: 'home',
    price_visible: false, picks_person: false, standard: 'trade',
    bht: 'listed',
    sections: ['hero', 'services', 'enquiry', 'reviews', 'products', 'posts', 'contact'],
    search: ['varmepumpe', 'luft til luft', 'luft til vann', 'ventilasjon',
             'balansert ventilasjon', 'aircondition', 'kjøling', 'montering'],
    tile: { icon: '❊', sub: 'Heat pumps · ventilation' },
    hero: {
      strip: 'Free site visit and a written quote before anything is fitted',
      kicker: 'VENTILATION & HEATING',
      title: 'A warm house.', em: 'A written price.',
      lead: 'Heat pumps, ventilation and cooling. Ask for a site visit — they look at the house, and the price comes in writing before anything is drilled.',
    },
    words: {
      service: 'job', services: 'What we do',
      staff_member: 'fitter', staff: 'The team',
      calendar: 'Jobs', appointment: 'job', appointments: 'Jobs',
      appointment_doc: 'Written agreement',
      visit: 'completed job', visits: 'Completed jobs', visit_unit: 'job',
      book: 'REQUEST A SITE VISIT', new: 'New job',
      history: 'Job history', line: 'Work and materials',
    },
    rights: [
      'Håndverkertjenesteloven gives you a written agreement and a price estimate before work starts.',
      'An estimate may not be exceeded by more than 15 per cent.',
      'Five years of complaint rights on work on a permanent dwelling.',
    ],
    papers: [
      ['Central approval', 'Ansvarsrett, checked against Direktoratet for byggkvalitet'],
      /* Refrigerant is the one thing this trade handles that a joiner does
         not, and handling it requires a certificate. It is the paper a
         customer should be able to ask for by name. */
      ['F-gas certificate', 'Required to work on refrigerant circuits'],
    ],
    /* He is coming to the house, and the house is the job. */
    disclosure: AT_HOME_D,
  },
  {
    code: 'restoration', name: 'Restoration & damage', layout: 'site_visit', theme: 'service',
    price_visible: false, picks_person: false, standard: 'trade',
    bht: 'listed',
    sections: ['hero', 'services', 'enquiry', 'reviews', 'products', 'posts', 'contact'],
    search: ['skade', 'vannskade', 'brannskade', 'sanering', 'skadedyr', 'utbedring'],
    tile: { icon: '◩', sub: 'Damage · callout' },
    hero: {
      strip: 'Round-the-clock callout — water and fire damage',
      kicker: 'RESTORATION & DAMAGE',
      title: 'When it has', em: 'already happened.',
      lead: 'Water, fire and damp. Report the damage, get somebody out, and keep the written agreement in one place.',
    },
    words: {
      service: 'job', services: 'What we do',
      staff_member: 'technician', staff: 'The team',
      calendar: 'Jobs', appointment: 'callout', appointments: 'Callouts',
      appointment_doc: 'Written agreement',
      visit: 'completed job', visits: 'Completed jobs', visit_unit: 'job',
      book: 'REPORT DAMAGE', new: 'New callout',
      history: 'Job history', line: 'Work and materials',
    },
    rights: [
      'Håndverkertjenesteloven gives you a written agreement and a price estimate before work starts.',
      'Insurance and excess must be stated in writing before a callout.',
    ],
    disclosure: AT_HOME_D,
  },
  {
    code: 'cleaning', name: 'Cleaning', layout: 'site_visit', theme: 'service',
    price_visible: true, picks_person: false, standard: 'trade',
    bht: 'listed',
    sections: ['hero', 'services', 'enquiry', 'reviews', 'products', 'posts', 'contact'],
    search: ['rengjøring', 'renhold', 'vask', 'flyttevask', 'vaskehjelp'],
    tile: { icon: '▧', sub: 'Homes · offices' },
    hero: {
      strip: 'Ask for a quote — you see what is included before you agree',
      kicker: 'CLEANING',
      title: 'Come home to', em: 'a clean house.',
      lead: 'One-off, regular or moving out. Ask for a quote and see what is included before you agree to it.',
    },
    words: {
      service: 'service', services: 'What we do',
      staff_member: 'cleaner', staff: 'The team',
      calendar: 'Jobs', appointment: 'visit', appointments: 'Visits',
      appointment_doc: 'Written agreement',
      visit: 'completed visit', visits: 'Completed visits', visit_unit: 'visit',
      book: 'REQUEST A QUOTE', new: 'New visit',
      history: 'Visit history',
    },
    disclosure: AT_HOME_D,
  },
  {
    code: 'childcare', name: 'Childcare & tutoring', layout: 'booking', theme: 'studio',
    price_visible: true, picks_person: true, standard: null,
    bht: 'listed',
    sections: ['hero', 'services', 'staff', 'booking', 'reviews', 'products', 'posts', 'contact'],
    search: ['barnepass', 'barnehage', 'leksehjelp', 'dagmamma', 'barn'],
    tile: { icon: '◔', sub: 'Childminding · tutoring' },
    hero: {
      strip: 'See who is free this week',
      kicker: 'CHILDCARE & TUTORING',
      title: 'Someone you can', em: 'leave them with.',
      lead: 'Childminding, after-school care and tutoring. See who is free, what it costs, and what was agreed.',
    },
    words: {
      service: 'session', services: 'Sessions',
      staff_member: 'carer', staff: 'The carers',
      appointment: 'session', appointments: 'Sessions',
      visit: 'completed session', visits: 'Completed sessions', visit_unit: 'session',
      book: 'BOOK A SESSION', new: 'New session',
      history: 'Session history',
    },
    disclosure: AT_HOME_D,
  },
  {
    code: 'legal', name: 'Law & legal', layout: 'engagement', theme: 'corporate',
    price_visible: false, picks_person: true, standard: 'lawyer',
    bht: 'risk',
    listing: 'advisor',
    sections: ['hero', 'services', 'staff', 'enquiry', 'standard', 'posts', 'contact'],
    search: ['advokat', 'jurist', 'juridisk', 'rettshjelp', 'arv', 'skilsmisse', 'kontrakt'],
    tile: { icon: '§', sub: 'Legal advice' },
    hero: {
      strip: 'Talk to a lawyer today — the first 15 minutes are free',
      kicker: 'LEGAL ADVICE',
      title: 'Find the lawyer.', em: 'Agree it today.',
      lead: 'Ring directly, book a meeting or ask for a quote. The engagement confirmation is agreed before the work starts.',
    },
    words: {
      service: 'service', services: 'Services',
      staff_member: 'lawyer', staff: 'The lawyers',
      customer: 'client', customers: 'Clients', customer_number: 'Client number',
      calendar: 'Meetings', appointment: 'meeting', appointments: 'Meetings',
      appointment_doc: 'Engagement confirmation',
      visit: 'held meeting', visits: 'Held meetings', visit_unit: 'meeting',
      book: 'REQUEST A MEETING', new: 'New meeting',
      file: 'client file', files: 'Client files', history: 'Case history',
    },
    rights: [
      'Regler for god advokatskikk apply to the whole engagement.',
      'Advokatnemnda hears complaints about fees and about conduct.',
      'The engagement confirmation must state what the work will cost, before it starts.',
    ],
    papers: [['Practising certificate', 'Checked against Advokattilsynet']],
    disclosure: NO_ADDRESS,
  },
  {
    code: 'accounting', name: 'Audit & accounting', layout: 'engagement', theme: 'corporate',
    price_visible: false, picks_person: true, standard: 'accounting',
    bht: 'risk',
    listing: 'advisor',
    sections: ['hero', 'services', 'staff', 'enquiry', 'standard', 'posts', 'contact'],
    search: ['regnskap', 'revisor', 'regnskapsfører', 'årsoppgjør', 'bokføring', 'lønn'],
    tile: { icon: '▤', sub: 'Audit · bookkeeping' },
    hero: {
      strip: 'Book a meeting with an auditor — the first 20 minutes are free',
      kicker: 'AUDIT & ACCOUNTING',
      title: 'The figures in order.', em: 'All year.',
      lead: 'Book a meeting with an auditor or a bookkeeper. The engagement agreement is settled before the work starts.',
    },
    words: {
      staff_member: 'adviser', staff: 'The advisers',
      customer: 'client', customers: 'Clients', customer_number: 'Client number',
      calendar: 'Meetings', appointment: 'meeting', appointments: 'Meetings',
      appointment_doc: 'Engagement agreement',
      visit: 'held meeting', visits: 'Held meetings', visit_unit: 'meeting',
      book: 'REQUEST A MEETING', new: 'New meeting',
      file: 'client file', files: 'Client files', history: 'Engagement history',
    },
    disclosure: NO_ADDRESS,
  },
  {
    code: 'architect', name: 'Architecture & design', layout: 'engagement', theme: 'corporate',
    price_visible: false, picks_person: true, standard: 'ns8401',
    bht: 'risk',
    listing: 'advisor',
    sections: ['hero', 'services', 'staff', 'enquiry', 'standard', 'posts', 'contact'],
    search: ['arkitekt', 'tegning', 'byggesøknad', 'prosjektering', 'tilbygg'],
    tile: { icon: '◱', sub: 'Drawings · permission' },
    hero: {
      strip: 'Free site visit and a quote before we draw anything',
      kicker: 'ARCHITECTURE & DESIGN',
      title: 'From an idea.', em: 'To planning permission.',
      lead: 'Book a site visit or a meeting. The commission is agreed under NS 8401 before anything is drawn.',
    },
    words: {
      service: 'commission', services: 'Commissions',
      staff_member: 'architect', staff: 'The architects',
      customer: 'client', customers: 'Clients', customer_number: 'Client number',
      calendar: 'Meetings', appointment: 'meeting', appointments: 'Meetings',
      appointment_doc: 'Engagement agreement',
      visit: 'held meeting', visits: 'Held meetings', visit_unit: 'meeting',
      book: 'REQUEST A SITE VISIT', new: 'New meeting',
      file: 'project file', files: 'Project files', history: 'Project history',
    },
    disclosure: NO_ADDRESS,
  },
  {
    code: 'engineer', name: 'Engineering & technical', layout: 'engagement', theme: 'corporate',
    price_visible: false, picks_person: true, standard: 'ns8402',
    bht: 'risk',
    listing: 'advisor',
    sections: ['hero', 'services', 'staff', 'enquiry', 'standard', 'posts', 'contact'],
    search: ['ingeniør', 'rådgiver', 'teknisk', 'konstruksjon', 'energi', 'brann'],
    tile: { icon: '◿', sub: 'Technical advice' },
    hero: {
      strip: 'Report by the agreed date — the deadline is in the commission',
      kicker: 'ENGINEERING & TECHNICAL',
      title: 'The right advice.', em: 'Before you build.',
      lead: 'Book a technical assessment or a meeting. The commission is agreed under NS 8402, with a date for the report.',
    },
    words: {
      service: 'commission', services: 'Commissions',
      staff_member: 'adviser', staff: 'The advisers',
      customer: 'client', customers: 'Clients', customer_number: 'Client number',
      calendar: 'Meetings', appointment: 'meeting', appointments: 'Meetings',
      appointment_doc: 'Engagement agreement',
      visit: 'held meeting', visits: 'Held meetings', visit_unit: 'meeting',
      book: 'REQUEST AN ASSESSMENT', new: 'New meeting',
      file: 'project file', files: 'Project files', history: 'Project history',
    },
    disclosure: NO_ADDRESS,
  },
];

/** What the platform can actually promise, whatever the trade. The prototype's
    hero promised free cancellation, Vipps payment and register checks; none of
    those are built, so none of them are here. These three are true of the
    software as it stands. */
export const TRUST = ['The price and the duration are shown before you book', 'The agreement and the receipt are yours, on My page', 'One account across every business you use'];

/** Every industry, in display order. */
export const INDUSTRIES = RECORDS.map(r => ({
  ...r,
  words: { ...BASE_WORDS, ...r.words },
  rights: [...BASE_RIGHTS, ...(r.rights || [])],
  papers: [...BASE_PAPERS, ...(r.papers || [])],
}));

const BY_CODE = Object.fromEntries(INDUSTRIES.map(i => [i.code, i]));

/** One industry record. Falls back to the first, so a business with an
    unknown industry code renders rather than crashes — a broken page is worse
    than a slightly wrong one. */
export function industry(code) {
  return BY_CODE[code] || INDUSTRIES[0];
}

/** The vocabulary for an industry. Every noun on every surface. */
export function words(code) {
  return industry(code).words;
}

/** The layouts in use. The website renderer has one file per entry here. */
/** The trades where you instruct a person rather than book a slot — the
    prototype's `listing: 'advisor'`. The front page's advisers section and
    anything else that needs to know asks here, so no surface has to write
    'legal' in a quoted string. */
export const ADVISORY = INDUSTRIES.filter(i => i.listing === 'advisor').map(i => i.code);

/**
 * May the customer choose WHO does the work?
 *
 * The question a booking screen has to answer before it offers a list of
 * people. You pick your hairdresser and you pick your lawyer, because the
 * person is the thing being chosen. You do not pick the joiner: you engage
 * the firm, and which of their people turns up on Tuesday is the firm's to
 * decide — they know who is on which site, who has the van and who is
 * certified for the work. A marketplace that lets a customer book a named
 * tradesman has made a promise on the firm's behalf that the firm never
 * made, and the firm finds out when the customer is standing in the kitchen
 * asking where Per is.
 *
 * A restaurant is the same shape from the other end: you are given a table,
 * not a waiter.
 *
 * Unknown trades default to true, which is the safe direction — it offers a
 * choice rather than silently removing one.
 */
export const picksPerson = code => industry(code)?.picks_person !== false;

/**
 * The blocks of a trade's public page, with one rule applied on the way out.
 *
 * ── A trade that does not let you pick the person does not name them ─────
 *
 * `picks_person` already decides whether a customer chooses WHO does the
 * work. The public page has to follow: a firm that allocates the job cannot
 * have a band of named people on the front of it with ratings and response
 * times, because every one of those cards is an implied offer the firm never
 * made. Somebody reads it, asks for Per, and the firm finds out when the
 * customer is standing in the kitchen.
 *
 * A restaurant is the same shape from the other end. A guest is given a
 * TABLE, not a waiter — and the page was listing six of them under a heading
 * that promised tables. Who is on the floor on Friday is the restaurant's
 * own information, it is of no use to a guest, and it is not the platform's
 * to publish.
 *
 * The rule is applied here rather than left to whoever edits a row, because
 * it had already been applied by hand to four trades and missed on the
 * fifth. A row that still lists `staff` under `picks_person: false` is now
 * simply not served it, and `industries.test.js` fails on the row as well so
 * the data gets corrected rather than silently patched over.
 */
export function sectionsFor(code) {
  const ind = industry(code);
  const list = ind?.sections || [];
  return picksPerson(code) ? list : list.filter(n => n !== 'staff');
}

/**
 * Is a trade of this kind normally named in the occupational-health
 * regulation's list?
 *
 * 'listed' — trades of this kind are named, so the duty to be attached to an
 *            approved bedriftshelsetjeneste applies whatever the risk
 *            assessment says.
 * 'risk'   — not named. The duty still applies where the risk assessment
 *            says it does; it is the risk that decides, not the absence of a
 *            name on a list.
 *
 * Read the qualifier in that first sentence carefully, because the whole
 * honesty of the feature is in it. The regulation lists NÆRINGSKODER, not
 * trade names, and the code a given business actually carries is that
 * business's own fact. So this column says what is normally true of a trade
 * and nothing about any particular business, and every screen that uses it
 * says the same and points at Arbeidstilsynet.
 *
 * Getting this wrong is expensive in both directions. Telling a law firm it
 * is breaking the law in order to sell it a subscription is the kind of
 * thing a platform does once; telling a building contractor it has no duty
 * is worse, because they might believe it.
 *
 * An unknown trade answers 'risk' — the answer that claims least.
 */
export const bhtDuty = code =>
  /* `industry()` falls back to the first record for an unknown code, which
     is the right default almost everywhere on this platform and exactly the
     wrong one here: the first record happens to be a listed trade, so an
     unrecognised code would come back claiming a statutory duty. Looked up
     directly instead, so an unknown trade answers 'risk' — the answer that
     claims least. */
  (BY_CODE[code]?.bht === 'listed' ? 'listed' : 'risk');

/** The family a trade belongs to, or null where it is its own. */
export const parentOf = code => {
  const p = industry(code)?.parent;
  return p && p !== code ? p : null;
};

/**
 * The words a customer might type for this trade, including the family's.
 *
 * A heat pump firm is a trades firm, so somebody searching "håndverker"
 * should find one — the speciality narrows what the business is called, not
 * what it is. Inheritance is one level deep on purpose: a tree of trades is a
 * taxonomy nobody asked for, and two levels is where those start.
 */
export const searchWords = code => {
  const own = industry(code)?.search || [];
  const up = parentOf(code);
  return up ? [...own, ...(industry(up).search || [])] : own;
};

export const LAYOUTS = [...new Set(INDUSTRIES.map(i => i.layout))];

/** The themes in use. Tokens only — never layout. */
export const THEMES = [...new Set(INDUSTRIES.map(i => i.theme))];

/* ── The disclosure lists, derived ────────────────────────────────────────
   `domain/disclosure.js` used to hold three hand-kept arrays and the PHP
   library held the same three as constants. They are one thing, so they are
   derived from one place now. The names are kept because the tests and the
   backend both use them. */
export const AT_HOME = INDUSTRIES.filter(i => i.disclosure.address).map(i => i.code);
export const AGE18 = INDUSTRIES.filter(i => i.disclosure.over18).map(i => i.code);
export const AGE = INDUSTRIES.filter(i => i.disclosure.age).map(i => i.code);
